import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  dunningDashboard, dunningTick, getDunningSequences, updateDunningSequence,
  getAgingReport,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Mail, Clock, CheckCircle2, AlertTriangle, Play, Loader2, Edit2, Save, X, Plus, Trash2,
  TrendingUp, Calendar, Send, Settings as Cog, HelpCircle, FileText, Sparkles,
} from 'lucide-react';
import HelpInfoDialog from '../components/help/HelpInfoDialog';
import { StatCard, SurfaceCard } from '../components/ui/filters-toolkit';
import PendingInvoicesList from '../components/receivables/PendingInvoicesList';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('es-ES'); } catch { return s; } };

const STATUS_META = {
  scheduled: { label: 'Programado', cls: 'bg-blue-100 text-blue-700', icon: Clock },
  sent: { label: 'Enviado', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelado', cls: 'bg-stone-100 text-stone-500', icon: X },
  skipped: { label: 'Omitido', cls: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  failed: { label: 'Falló', cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
};

const ReceivablesPage = () => {
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [aging, setAging] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [editingSeq, setEditingSeq] = useState(null);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('pending');
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, ag, seq] = await Promise.all([
        dunningDashboard(),
        getAgingReport().catch(() => ({ data: null })),
        getDunningSequences(),
      ]);
      setDash(d.data);
      setAging(ag.data);
      setSequences(seq.data?.items || []);
    } catch (e) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleTick = async () => {
    setRunning(true);
    try {
      const r = await dunningTick();
      toast.success(`Procesados: ${r.data.processed} | Enviados: ${r.data.sent} | Omitidos: ${r.data.skipped} | Fallos: ${r.data.failed}`);
      load();
    } catch (e) {
      toast.error('Error procesando dunning');
    } finally {
      setRunning(false);
    }
  };

  const handleSaveSeq = async () => {
    if (!editingSeq) return;
    try {
      await updateDunningSequence(editingSeq.id, { steps: editingSeq.steps, name: editingSeq.name });
      toast.success('Secuencia guardada');
      setEditingSeq(null);
      load();
    } catch (e) {
      toast.error('Error guardando');
    }
  };

  const addStep = () => {
    setEditingSeq({
      ...editingSeq,
      steps: [...(editingSeq.steps || []), {
        days_offset: 0, name: 'Nuevo paso',
        subject: 'Recordatorio factura {numero_factura}',
        html: '<p>Hola {cliente}, ...</p>',
      }]
    });
  };

  const updateStep = (idx, field, value) => {
    const newSteps = [...editingSeq.steps];
    newSteps[idx] = { ...newSteps[idx], [field]: field === 'days_offset' ? parseInt(value) || 0 : value };
    setEditingSeq({ ...editingSeq, steps: newSteps });
  };

  const removeStep = (idx) => {
    const newSteps = editingSeq.steps.filter((_, i) => i !== idx);
    setEditingSeq({ ...editingSeq, steps: newSteps });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="receivables-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-[#FF6B4A]" />
            Por Cobrar
          </h1>
          <p className="text-sm text-stone-500 mt-1">Aging report + recordatorios automáticos (Dunning)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowHelp(true)} className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="receivables-help-btn">
            <HelpCircle className="w-4 h-4 mr-1.5" />¿Qué es esto?
          </Button>
          <Button onClick={handleTick} disabled={running} className="bg-[#FF6B4A] text-white" data-testid="dunning-tick-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Procesar recordatorios ahora
          </Button>
        </div>
      </div>

      <div className="flex gap-1 bg-white rounded-2xl border border-stone-100 shadow-sm p-1 w-fit flex-wrap">
        {[
          { k: 'pending', l: 'Facturas pendientes' },
          { k: 'overview', l: 'Aging + Dunning' },
          { k: 'sequence', l: 'Secuencia recordatorios' },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${tab === t.k ? 'bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white shadow-md shadow-orange-200/40' : 'text-stone-600 hover:bg-stone-50'}`}
            data-testid={`recv-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'pending' && <PendingInvoicesList />}

      {tab === 'overview' && (
        <>
          {/* Aging — colorful stat cards */}
          {aging && (
            <div data-testid="aging-section">
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-3">
                Aging · Pendiente {fmt(aging.total_pending)}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { k: '0-30', label: '0-30 días', icon: CheckCircle2, gradient: 'bg-gradient-to-br from-emerald-50 to-teal-50', iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
                  { k: '31-60', label: '31-60 días', icon: Clock, gradient: 'bg-gradient-to-br from-amber-50 to-yellow-50', iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-500' },
                  { k: '61-90', label: '61-90 días', icon: AlertTriangle, gradient: 'bg-gradient-to-br from-orange-50 to-amber-50', iconBg: 'bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]' },
                  { k: '+90', label: '+90 días', icon: AlertTriangle, gradient: 'bg-gradient-to-br from-rose-50 to-red-50', iconBg: 'bg-gradient-to-br from-rose-500 to-red-500' },
                  { k: 'no_due', label: 'Sin vencimiento', icon: FileText, gradient: 'bg-gradient-to-br from-stone-50 to-stone-100', iconBg: 'bg-gradient-to-br from-stone-500 to-stone-600' },
                ].map((b) => {
                  const v = aging.buckets?.[b.k] || { total: 0, count: 0 };
                  return (
                    <StatCard
                      key={b.k}
                      label={b.label}
                      value={fmt(v.total)}
                      sub={`${v.count} factura${v.count === 1 ? '' : 's'}`}
                      icon={b.icon}
                      gradient={b.gradient}
                      iconBg={b.iconBg}
                      testid={`aging-bucket-${b.k}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Dunning dashboard — colorful stat cards */}
          {dash && (
            <div data-testid="dunning-section">
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-3">
                Recordatorios automáticos
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {[
                  { k: 'scheduled', label: 'Programados', icon: Clock, gradient: 'bg-gradient-to-br from-sky-50 to-blue-50', iconBg: 'bg-gradient-to-br from-sky-500 to-blue-500' },
                  { k: 'sent', label: 'Enviados', icon: CheckCircle2, gradient: 'bg-gradient-to-br from-emerald-50 to-teal-50', iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
                  { k: 'skipped', label: 'Omitidos', icon: AlertTriangle, gradient: 'bg-gradient-to-br from-amber-50 to-yellow-50', iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-500' },
                  { k: 'failed', label: 'Fallidos', icon: AlertTriangle, gradient: 'bg-gradient-to-br from-rose-50 to-red-50', iconBg: 'bg-gradient-to-br from-rose-500 to-red-500' },
                  { k: 'cancelled', label: 'Cancelados', icon: X, gradient: 'bg-gradient-to-br from-stone-50 to-stone-100', iconBg: 'bg-gradient-to-br from-stone-500 to-stone-600' },
                ].map((s) => (
                  <StatCard
                    key={s.k}
                    label={s.label}
                    value={dash.counts?.[s.k] || 0}
                    icon={s.icon}
                    gradient={s.gradient}
                    iconBg={s.iconBg}
                    testid={`dunning-stat-${s.k}`}
                  />
                ))}
              </div>
              {dash.next_run && (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-sm flex items-center gap-3" data-testid="dunning-next-run">
                  <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    Próximo envío: <strong>{dash.next_run.step_name}</strong> el {fmtDate(dash.next_run.scheduled_at)}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'sequence' && (
        <SurfaceCard className="!p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm font-bold uppercase text-stone-600">Plantilla por defecto</h2>
            {!editingSeq && sequences[0] && (
              <Button size="sm" variant="outline" onClick={() => setEditingSeq({ ...sequences[0] })} data-testid="edit-sequence-btn">
                <Edit2 className="w-4 h-4 mr-1" /> Editar
              </Button>
            )}
            {editingSeq && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditingSeq(null)}><X className="w-4 h-4" /></Button>
                <Button size="sm" onClick={handleSaveSeq} className="bg-[#FF6B4A] text-white" data-testid="save-sequence-btn"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {(editingSeq?.steps || sequences[0]?.steps || []).map((step, idx) => (
              <div key={idx} className="border border-stone-200 rounded-xl p-3" data-testid={`seq-step-${idx}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 rounded-full bg-[#FF6B4A]/10 text-[#FF6B4A] font-bold flex items-center justify-center text-sm">{idx + 1}</span>
                  {editingSeq ? (
                    <>
                      <Input value={step.name} onChange={(e) => updateStep(idx, 'name', e.target.value)} className="flex-1" />
                      <Input type="number" value={step.days_offset} onChange={(e) => updateStep(idx, 'days_offset', e.target.value)} className="w-24" placeholder="días" />
                      <span className="text-xs text-stone-500">días vs vencimiento</span>
                      <Button size="sm" variant="ghost" onClick={() => removeStep(idx)} className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-stone-800">{step.name}</span>
                      <span className="text-xs text-stone-500">
                        {step.days_offset === 0 ? 'día del vencimiento' : step.days_offset < 0 ? `${Math.abs(step.days_offset)} días antes` : `+${step.days_offset} días vencido`}
                      </span>
                    </>
                  )}
                </div>
                {editingSeq && (
                  <div className="space-y-2 mt-2 ml-11">
                    <Input value={step.subject} onChange={(e) => updateStep(idx, 'subject', e.target.value)} placeholder="Asunto del email" />
                    <Textarea value={step.html} onChange={(e) => updateStep(idx, 'html', e.target.value)} rows={3} placeholder="HTML del cuerpo (variables: {cliente}, {numero_factura}, {importe}, {fecha_vencimiento}, {dias_vencido}, {link_pago}, {empresa})" />
                  </div>
                )}
              </div>
            ))}
            {editingSeq && (
              <Button variant="outline" onClick={addStep} className="w-full" data-testid="add-step-btn">
                <Plus className="w-4 h-4 mr-1" /> Añadir paso
              </Button>
            )}
          </div>
          <p className="text-xs text-stone-400 mt-4">
            <Cog className="w-3 h-3 inline mr-1" />
            Variables: {'{cliente}'}, {'{numero_factura}'}, {'{importe}'}, {'{fecha_vencimiento}'}, {'{dias_vencido}'}, {'{link_pago}'}, {'{empresa}'}
          </p>
        </SurfaceCard>
      )}

      <HelpInfoDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        icon={TrendingUp}
        title="¿Qué es Por Cobrar?"
        intro={(
          <>
            <p><strong>Reclama automáticamente las facturas impagadas</strong>. En vez de tener que estar enviando
              emails de "oye, recuérdame", el sistema lo hace por ti siguiendo una secuencia que tú defines.</p>
            <p className="mt-2">También ves el <strong>Aging Report</strong>: cuánto te deben tus clientes agrupado por antigüedad de la deuda.</p>
          </>
        )}
        sections={[
          {
            icon: Calendar, iconColor: '#FF6B4A', title: 'Aging Report — qué muestra',
            body: [
              '<strong>0–30 días</strong>: facturas vencidas hace menos de un mes (normal)',
              '<strong>31–60 días</strong>: empieza a ser preocupante',
              '<strong>61–90 días</strong>: alta probabilidad de problemas',
              '<strong>+90 días</strong>: deuda morosa, vía judicial o cesión',
            ],
          },
          {
            icon: Send, iconColor: '#7C3AED', title: 'Secuencia de recordatorios (Dunning)',
            body: 'Defines una lista de mensajes ordenados por días tras el vencimiento. Ejemplo: "Día 3 → recordatorio amable", "Día 14 → segundo aviso con copia adjunta", "Día 30 → última notificación antes de acciones legales". El sistema los envía solo en el momento exacto.',
          },
          {
            icon: Sparkles, iconColor: '#10B981', title: 'Lo automático que hace',
            body: [
              'Detecta facturas vencidas cada día',
              'Envía el email correspondiente del paso actual',
              'Si el cliente paga, <strong>cancela el resto de la secuencia</strong>',
              'Te avisa de impagos persistentes (+60 días sin respuesta)',
            ],
          },
          {
            icon: FileText, iconColor: '#F59E0B', title: 'Plantillas personalizables',
            body: 'Cada paso tiene asunto + cuerpo del email con variables ({{cliente}}, {{importe}}, {{numero}}, {{dias_vencido}}). Puedes editarlo en la pestaña "Secuencia recordatorios" para adaptar el tono de tu empresa.',
          },
        ]}
      />
    </div>
  );
};

export default ReceivablesPage;
